import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
    Flex,
    Button,
    Table,
    ConfigProvider,
    Space,
    Divider,
    Typography,
    Card,
    Carousel,
} from '@app/ui';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import * as format from '@app/utils/formatters';
import shortCurrency from '@app/utils/shortCurrency';
import '@app/components/common/modals/PostModal.scss';
import { Rightsholder, RightsholderPlacement, Sponsor } from '@app/types/api';
import { findAllMentionsInContext } from '../../utils/broadcastsUtils';

const domNode = document.getElementById('broadcast-details-modal');

const isFiniteMetric = (value?: number) =>
    typeof value === 'number' && Number.isFinite(value);

const formatMetricNumber = (value?: number) => {
    if (!isFiniteMetric(value) || value === 0) {
        return '-';
    }

    return format.decimal(value);
};

const formatMetricImpressions = (value?: number) => {
    if (!isFiniteMetric(value) || value === 0) {
        return '-';
    }

    return format.abbreviatedDecimal(value);
};

const formatMetricCurrency = (
    value: number | undefined,
    currencyCode: string,
) => {
    if (!isFiniteMetric(value) || value === 0) {
        return '-';
    }

    return shortCurrency(value, 1, currencyCode);
};

export const getDownloadButtonLabelKey = (
    disableDownload: boolean,
    isDownloadLoading: boolean,
) => {
    if (disableDownload) {
        return 'measurement:broadcastExposure.detailsModal.downloadUnavailable';
    }

    if (isDownloadLoading) {
        return 'measurement:broadcastExposure.detailsModal.loading';
    }

    return 'measurement:broadcastExposure.detailsModal.downloadPdf';
};

interface DetailsModalProps {
    broadcastName: string;
    frameUrls: string[];
    verbalMentionsExample?: {
        mention?: string;
        context: string[];
        videoUrls: string[];
        hasAvailableClips: boolean;
    };
    rightsholder: Rightsholder;
    sponsor: Sponsor;
    placement: RightsholderPlacement;
    disableDownload: boolean;
    isDownloadingReport: boolean;
    isLoadingReport: boolean;
    total: {
        exposures?: number;
        exposuresImpressions?: number;
        duration?: number;
        sponsorMediaValue?: number;
    };
    currencyCode: string;
    networks: string[];
    onTrackVideoPlayed: () => void;
    getReport: (frameIndex?: number) => Promise<void>;
    downloadFrameImage: (frameIndex?: number) => Promise<void>;
    onClose: () => void;
}

function DetailsModal({
    broadcastName,
    frameUrls,
    verbalMentionsExample,
    rightsholder,
    sponsor,
    placement,
    disableDownload,
    isDownloadingReport,
    isLoadingReport,
    total,
    currencyCode,
    networks,
    onTrackVideoPlayed,
    getReport,
    downloadFrameImage,
    onClose,
}: DetailsModalProps) {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement[]>([]);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [isVideoPlayingTracked, setIsVideoPlayingTracked] = useState(false);
    const [isDownloadingImage, setIsDownloadingImage] = useState(false);
    useEffect(() => {
        document.body.classList.add('-noscroll');
        return () => {
            document.body.classList.remove('-noscroll');
        };
    }, []);

    useEffect(() => {
        const currentVideo = videoRef.current[currentVideoIndex];
        if (currentVideo) {
            currentVideo.onplay = () => {
                if (!isVideoPlayingTracked) {
                    setIsVideoPlayingTracked(true);
                    onTrackVideoPlayed();
                }
            };
        }
    }, [videoRef.current, currentVideoIndex, isVideoPlayingTracked]);

    const isDownloadLoading = isDownloadingReport || isLoadingReport;
    const disableDownloadButton = disableDownload || isDownloadLoading;
    const currentFrameUrl = verbalMentionsExample
        ? undefined
        : frameUrls[currentVideoIndex];

    const onDownloadImage = async () => {
        setIsDownloadingImage(true);
        try {
            await downloadFrameImage(currentVideoIndex);
        } finally {
            setIsDownloadingImage(false);
        }
    };
    const vmVideoUrls = verbalMentionsExample?.videoUrls ?? [];
    const hasAvailableVmClips =
        !!verbalMentionsExample &&
        verbalMentionsExample.hasAvailableClips &&
        vmVideoUrls.length > 0;

    // Get context substrings with mentions
    const contextSubstrings =
        verbalMentionsExample &&
        findAllMentionsInContext(
            verbalMentionsExample.context[currentVideoIndex] ??
                verbalMentionsExample.context[0] ??
                '',
            verbalMentionsExample.mention,
        );

    const showCarouselNav =
        (hasAvailableVmClips && vmVideoUrls.length > 1) ||
        (!verbalMentionsExample && frameUrls.length > 1);

    return ReactDOM.createPortal(
        <div
            className="modal-overlay modal-shown"
            style={{ zIndex: 1200 }}
            onClick={onClose}
        >
            <div
                className="modal-v2-wrapper"
                style={{
                    maxWidth: 600,
                }}
                onClick={e => e.stopPropagation()}
            >
                <Button
                    className="button-close-v2"
                    color="primary"
                    variant="outlined"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    size={'middle'}
                />
                <Flex
                    className="modal-v2"
                    vertical
                    style={{
                        maxWidth: 600,
                    }}
                >
                    <div
                        style={{ position: 'relative' }}
                        className="custom-carousel-container"
                    >
                        <ConfigProvider
                            theme={{
                                components: {
                                    Carousel: {
                                        arrowSize: 25,
                                        arrowOffset: 4,
                                        dotActiveWidth: 24,
                                        dotHeight: 6,
                                    },
                                },
                            }}
                        >
                            <Carousel
                                infinite={false}
                                arrows={showCarouselNav}
                                dots={showCarouselNav}
                                beforeChange={() => {
                                    const currentVideo =
                                        videoRef.current[currentVideoIndex];
                                    if (currentVideo) {
                                        currentVideo.pause();
                                    }
                                }}
                                afterChange={currentVideoIndex =>
                                    setCurrentVideoIndex(currentVideoIndex)
                                }
                                style={{ width: 600, padding: '0 20px' }}
                            >
                                {verbalMentionsExample
                                    ? hasAvailableVmClips
                                        ? vmVideoUrls.map((videoUrl, index) => (
                                              <video
                                                  src={videoUrl}
                                                  key={videoUrl}
                                                  width="100%"
                                                  height="324px"
                                                  ref={videoElement => {
                                                      if (videoRef.current) {
                                                          videoRef.current[
                                                              index
                                                          ] = videoElement;
                                                      }
                                                  }}
                                                  controls
                                              >
                                                  <p>
                                                      {t(
                                                          'measurement:broadcastExposure.detailsModal.videoNotSupported',
                                                      )}{' '}
                                                      <a
                                                          href={videoUrl}
                                                          target="_blank"
                                                          rel="noreferrer"
                                                      >
                                                          {t(
                                                              'measurement:broadcastExposure.detailsModal.linkToVideo',
                                                          )}
                                                      </a>{' '}
                                                      {t(
                                                          'measurement:broadcastExposure.detailsModal.instead',
                                                      )}
                                                  </p>
                                              </video>
                                          ))
                                        : [
                                              <Flex
                                                  key="vm-clip-unavailable"
                                                  vertical
                                                  justify="center"
                                                  align="center"
                                                  style={{
                                                      width: '100%',
                                                      height: '324px',
                                                      backgroundColor:
                                                          '#F5F5F5',
                                                      padding: 24,
                                                      textAlign: 'center',
                                                  }}
                                              >
                                                  <Typography.Title
                                                      level={5}
                                                      style={{
                                                          marginBottom: 8,
                                                      }}
                                                  >
                                                      {t(
                                                          'measurement:broadcastExposure.detailsModal.clipUnavailableTitle',
                                                      )}
                                                  </Typography.Title>
                                                  <Typography.Text type="secondary">
                                                      {t(
                                                          'measurement:broadcastExposure.detailsModal.clipUnavailableDescription',
                                                      )}
                                                  </Typography.Text>
                                              </Flex>,
                                          ]
                                    : frameUrls.map(frameUrl => (
                                          <img
                                              src={frameUrl}
                                              width="100%"
                                              key={frameUrl}
                                              alt={t(
                                                  'measurement:broadcastExposure.detailsModal.sampleExposureFrame',
                                              )}
                                          />
                                      ))}
                            </Carousel>
                        </ConfigProvider>
                    </div>
                    <Flex
                        className="modal-v2-actions"
                        justify="center"
                        align="center"
                    >
                        <Button
                            icon={<DownloadOutlined />}
                            loading={isDownloadLoading}
                            disabled={disableDownloadButton}
                            onClick={() => getReport(currentVideoIndex)}
                            title={t(
                                'measurement:socialMediaExposure.postModal.actions.download',
                            )}
                            size="small"
                            type="primary"
                            color="primary"
                            variant="outlined"
                        >
                            {t(
                                getDownloadButtonLabelKey(
                                    disableDownload,
                                    isDownloadLoading,
                                ),
                            )}
                        </Button>
                        {!verbalMentionsExample && (
                            <Button
                                data-test="download-frame-jpeg"
                                icon={<DownloadOutlined />}
                                loading={isDownloadingImage}
                                disabled={!currentFrameUrl}
                                onClick={onDownloadImage}
                                title={t(
                                    'measurement:socialMediaExposure.postModal.actions.downloadJpeg',
                                )}
                                size="small"
                                type="primary"
                                color="primary"
                                variant="outlined"
                                style={{ marginLeft: 8 }}
                            >
                                {t(
                                    'measurement:broadcastExposure.detailsModal.downloadJpeg',
                                )}
                            </Button>
                        )}
                        <Flex
                            className="modal-v2-caption-container"
                            style={{
                                padding: 0,
                                textAlign: 'center',
                                paddingLeft: 12,
                            }}
                            flex={1}
                            justify="flex-start"
                            align="baseline"
                        >
                            <Typography.Text
                                style={{
                                    fontWeight: 400,
                                }}
                            >
                                {broadcastName}
                            </Typography.Text>
                        </Flex>
                    </Flex>
                    {verbalMentionsExample && (
                        <Flex style={{ width: '100%' }}>
                            <Card
                                type="inner"
                                size="small"
                                title={t(
                                    'measurement:broadcastExposure.detailsModal.context',
                                )}
                                style={{
                                    width: '100%',
                                    borderColor: 'rgba(0, 0, 0, 0.15)',
                                }}
                                styles={{
                                    header: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        borderColor: 'rgba(0, 0, 0, 0.06)',
                                        textAlign: 'center',
                                    },
                                }}
                            >
                                {verbalMentionsExample.context ? (
                                    <Typography>
                                        <Typography.Paragraph>
                                            {contextSubstrings.map((e, i) => {
                                                if (e.isMention) {
                                                    return (
                                                        <Typography.Text
                                                            key={`${i}-${e.substring}`}
                                                            mark
                                                        >
                                                            {e.substring}
                                                        </Typography.Text>
                                                    );
                                                }
                                                return e.substring;
                                            })}
                                        </Typography.Paragraph>
                                    </Typography>
                                ) : (
                                    <Typography.Text strong>
                                        {t(
                                            'measurement:broadcastExposure.detailsModal.noContextAvailable',
                                        )}
                                    </Typography.Text>
                                )}
                            </Card>
                        </Flex>
                    )}
                    {verbalMentionsExample?.mention && (
                        <Flex style={{ width: '100%', margin: '16px auto' }}>
                            <Card
                                size="small"
                                style={{
                                    width: '100%',
                                    backgroundColor: 'rgba(230, 244, 255, 1)',
                                    borderColor: 'rgba(0, 159, 223, 1)',
                                }}
                                styles={{
                                    body: {
                                        padding: '6px 12px',
                                        height: '33px',
                                    },
                                }}
                            >
                                <Typography>
                                    <Typography.Paragraph>
                                        <i
                                            className="fas fa-info-circle"
                                            style={{
                                                fontSize: 16,
                                                color: 'rgba(0, 159, 223, 1)',
                                            }}
                                        />{' '}
                                        <Trans
                                            i18nKey="measurement:broadcastExposure.detailsModal.verbalExposuresSummary"
                                            values={{
                                                mention:
                                                    verbalMentionsExample.mention,
                                            }}
                                            components={{
                                                strong: (
                                                    <Typography.Text strong />
                                                ),
                                            }}
                                        />
                                    </Typography.Paragraph>
                                </Typography>
                            </Card>
                        </Flex>
                    )}
                    <Flex className="modal-v2-stats-data">
                        <Flex className="modal-v2-stats-header-row">
                            <Space className="modal-v2-stats-header">
                                <Typography.Text strong>
                                    {t(
                                        'measurement:broadcastExposure.detailsModal.rightsholder',
                                    )}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-header">
                                <Typography.Text strong>
                                    {t(
                                        'measurement:broadcastExposure.detailsModal.sponsor',
                                    )}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-header">
                                <Typography.Text strong>
                                    {t(
                                        'measurement:broadcastExposure.detailsModal.placement',
                                    )}
                                </Typography.Text>
                            </Space>
                        </Flex>
                        <Divider className="modal-v2-divider" />
                        <Flex className="modal-v2-stats-header-row">
                            <Space className="modal-v2-stats-body">
                                <Typography.Text style={{ fontWeight: 400 }}>
                                    {rightsholder.name}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-body">
                                <Typography.Text style={{ fontWeight: 400 }}>
                                    {sponsor.name}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-body">
                                <Typography.Text style={{ fontWeight: 400 }}>
                                    {placement.name}
                                </Typography.Text>
                            </Space>
                        </Flex>
                    </Flex>
                    <ConfigProvider
                        theme={{
                            components: {
                                Table: {
                                    headerBg: '#e1effa',
                                    borderColor: '#0000000F',
                                },
                            },
                        }}
                    >
                        <Table
                            size="small"
                            pagination={false}
                            bordered
                            rowHoverable={false}
                            style={{
                                width: '100%',
                            }}
                            loading={isLoadingReport}
                            dataSource={[
                                {
                                    key: 0,
                                    exposures: formatMetricNumber(
                                        total.exposures,
                                    ),
                                    impressions: formatMetricImpressions(
                                        total.exposuresImpressions,
                                    ),
                                    duration: formatMetricNumber(
                                        total.duration,
                                    ),
                                    smv: formatMetricCurrency(
                                        total.sponsorMediaValue,
                                        currencyCode,
                                    ),
                                },
                            ]}
                            columns={[
                                {
                                    title: t(
                                        'measurement:broadcastExposure.detailsModal.exposures',
                                    ),
                                    dataIndex: 'exposures',
                                    width: '25%',
                                    align: 'center',
                                },
                                {
                                    title: t(
                                        'measurement:broadcastExposure.detailsModal.impressions',
                                    ),
                                    dataIndex: 'impressions',
                                    width: '25%',
                                    align: 'center',
                                },
                                {
                                    title: t(
                                        'measurement:broadcastExposure.detailsModal.duration',
                                    ),
                                    dataIndex: 'duration',
                                    width: '25%',
                                    align: 'center',
                                },
                                {
                                    title: t(
                                        'measurement:broadcastExposure.detailsModal.smv',
                                    ),
                                    dataIndex: 'smv',
                                    width: '25%',
                                    align: 'center',
                                },
                            ]}
                        />
                        <Flex
                            vertical
                            style={{
                                borderRadius: '0 0 12px 12px',
                                border: '1px solid #e2e2e2',
                                borderTopWidth: 0,
                                width: '100%',
                            }}
                            justify="center"
                            align="center"
                        >
                            <Flex
                                style={{
                                    width: '100%',
                                    background: '#f0f0f0',
                                    height: 40,
                                    padding: 24,
                                }}
                                justify="center"
                                align="center"
                            >
                                <Typography.Text strong>
                                    {t(
                                        'measurement:broadcastExposure.detailsModal.networks',
                                    )}
                                </Typography.Text>
                            </Flex>

                            <Typography.Text
                                className="modal-v2-network"
                                style={{
                                    padding: 12,
                                    textAlign: 'center',
                                }}
                            >
                                {networks.join(' • ')}
                            </Typography.Text>
                        </Flex>
                    </ConfigProvider>
                </Flex>
            </div>
        </div>,
        domNode,
    );
}

export default DetailsModal;
